/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Marketing constraints:
 * - Keep language focused on operational outcomes for contractor teams.
 * - Do not promise an automatic budget-actuals engine where not implemented.
 * - Describe financial outcomes as visibility and decision support unless
 *   behavior is explicitly confirmed as automated.
 */
"use client";
import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Briefcase,
  Wrench,
  DollarSign,
  Clock,
  Calendar,
  ClipboardList,
  BarChart3,
  FileText,
  Calculator,
  Bot,
  Receipt,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Projects & Services",
  title: "Run the job without",
  titleHighlight: "losing control of costs.",
  description:
    "When the client says yes, the real work starts: assign crews, manage the schedule, track labor and purchasing, and keep billing tied to actual progress. CC gives your team one place to run that entire job lifecycle.",

  featureGrid: {
    title: "Everything in the project workspace",
    items: [
      {
        icon: Briefcase,
        title: "Job Health Snapshot",
        description:
          "See budget status, task progress, and billing position at a glance before problems compound.",
      },
      {
        icon: ClipboardList,
        title: "Scope and Task Clarity",
        description:
          "Keep scope details and task expectations clear so office and field teams stop working from conflicting assumptions.",
      },
      {
        icon: FileText,
        title: "Assigned Work Orders",
        description:
          "Assign work with due dates and ownership so each crew knows what is due next.",
      },
      {
        icon: DollarSign,
        title: "Cost Tracking",
        description:
          "Track labor, materials, subs, and equipment during the job so overruns are caught while you can still act.",
      },
      {
        icon: Wrench,
        title: "Service Work Control",
        description:
          "Run recurring and one-off service visits without calendar spreadsheets and disconnected notes.",
      },
      {
        icon: Clock,
        title: "Crew Time and Approvals",
        description:
          "Capture crew hours with approvals so labor costs are trusted before payroll and billing.",
      },
      {
        icon: Calendar,
        title: "Timeline and Dispatch Visibility",
        description: "Coordinate timelines and visits in one scheduling view.",
      },
      {
        icon: BarChart3,
        title: "Activity History",
        description:
          "See who changed what so handoffs stay clean and disputes are easier to resolve.",
      },
      {
        icon: Receipt,
        title: "Progress-Based Billing",
        description:
          "Invoice from delivered work and keep payment visibility connected to the job.",
      },
    ],
  },

  workflow: {
    title: "What happens after the client says yes",
    description:
      "A practical operating rhythm for construction teams: kickoff, assign, control spend, and close out without losing margin visibility.",
    steps: [
      {
        label: "Kickoff",
        title: "Turn sold work into an execution plan",
        description:
          "Start with approved scope, budget targets, and timeline expectations already connected so crews can execute without guesswork.",
        details: [
          "Approved estimate context carries into the job workspace",
          "Scope, budget, and schedule start aligned",
          "Ownership is clear before field work begins",
          "Consistent kickoff process across every job",
        ],
      },
      {
        label: "Assign",
        title: "Create and manage job tasks",
        description:
          "Break scope into assigned tasks with due dates so teams know what to do today, this week, and next.",
        details: [
          "Task ownership and due-date visibility",
          "Status-based progress tracking",
          "Crew workload visibility across active jobs",
          "Fewer missed handoffs between office and field",
        ],
      },
      {
        label: "Control Costs",
        title: "Track actuals while work is in motion",
        description:
          "Capture real costs as work happens so margin drift appears early enough to correct.",
        details: [
          "Labor, material, equipment, and subcontractor cost visibility",
          "Category-level variance awareness",
          "Remaining-budget context for daily decisions",
          "Faster intervention when costs move off plan",
        ],
      },
      {
        label: "Close Out",
        title: "Bill cleanly and learn from outcomes",
        description:
          "Close with complete billing and cost outcomes so your next bid is informed by what actually happened on the job.",
        details: [
          "Billing stays tied to delivered scope",
          "Budget-to-actual story is clear at close-out",
          "Final cost variance is visible to inform the next estimate",
          "Cleaner close-out records for warranty and follow-up work",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: Wrench,
      title: "Service Work That Stays Organized",
      description:
        "Manage recurring and one-off service work with clear visit history, scheduling continuity, and cost context.",
      bullets: [
        "Recurring and one-time service support",
        "Visit tracking from scheduled to completed",
        "Service-level cost visibility",
        "Cleaner dispatch continuity for field teams",
      ],
    },
    {
      icon: Calendar,
      title: "Scheduling That Matches Real Field Operations",
      description:
        "Coordinate job timelines and service visits in one scheduling flow to reduce conflicts and missed appointments.",
      bullets: [
        "Project timeline visibility",
        "Service visit scheduling control",
        "Rescheduling support without chaos",
        "Cross-job planning clarity",
      ],
    },
    {
      icon: Clock,
      title: "Labor Time Tracking With Approval Control",
      description:
        "Capture crew time with approval controls so labor costs stay accurate and accountable.",
      bullets: [
        "Project-level time logging",
        "Manager approval workflows",
        "Faster review and correction cycles",
        "Better labor-cost confidence before billing",
      ],
    },
    {
      icon: FileText,
      title: "Faster Job Setup With Less Re-Entry",
      description:
        "Start jobs from existing estimate and scope context so kickoff is faster and less manual.",
      bullets: [
        "Estimate context carried into execution",
        "Scope context visible to the whole team",
        "Linked project documentation",
        "Reduced re-entry at kickoff",
      ],
    },
  ],

  connections: {
    title: "How execution ties back to the business",
    items: [
      {
        icon: Calculator,
        flow: "Estimate → Project",
        description:
          "Sold work moves into execution with budget and scope continuity intact.",
      },
      {
        icon: DollarSign,
        flow: "Actuals → Cost Database",
        description:
          "Real job outcomes stay visible for future pricing decisions and estimate accuracy.",
      },
      {
        icon: Receipt,
        flow: "Project → Invoice",
        description:
          "Billing stays aligned with delivered work for cleaner invoicing and faster collections.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Estimates & Takeoffs",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: Receipt,
    },
    {
      label: "AI Assistant (Hank)",
      href: "/features/ai-assistant",
      icon: Bot,
    },
    {
      label: "Schedule & Dispatch",
      href: "/features/schedule",
      icon: Calendar,
    },
    {
      label: "Field Ops & Time Tracking",
      href: "/features/field-ops",
      icon: Clock,
    },
    {
      label: "Cost Database",
      href: "/features/cost-database",
      icon: DollarSign,
    },
  ],
};

export function ProjectManagementPageClient() {
  return <FeaturePageTemplate config={config} />;
}
